import { COMPETITOR_POS } from '../../../../shared/enums';
import type { IsoDate, Lead, Signal } from '../../../../shared/types';
import { leadLocations } from '../../data/geocode';
import { diffDays } from '../../logic/dates';
import { formatDistance } from '../../logic/route';
import type { LatLng } from '../../logic/route';
import { distanceText, hiddenText, leadsNearby } from '../../logic/signalRank';
import { signalEventLabel } from '../../logic/signals';
import { leadTitle, row, section } from '../../ui/components';
import type { Child } from '../../ui/dom';
import { POS_LABELS } from '../../ui/labels';

/** The desk's reading of one business (D74, D80): its row line, and the detail pane's parts beyond the
 *  card — the map where it is, and the rep's leads within 500 m, placed as the phone already placed them. */

/** "Orange · New license · 6 d · Toast · 0.8 mi", or a hidden row's reason in place of the event. */
export const rowMeta = (s: Signal, today: IsoDate, anchor: LatLng | null): string => {
  const days = diffDays(today, s.filed_on);
  const pos = s.detected_pos;
  return [
    s.city,
    s.non_merchant ? 'Probably not a merchant' : null,
    s.bucket === 'hidden' ? hiddenText(s) : signalEventLabel(s),
    days <= 0 ? 'today' : `${days} d`,
    pos && COMPETITOR_POS.includes(pos) ? POS_LABELS[pos] : null,
    distanceText(s, anchor),
  ]
    .filter(Boolean)
    .join(' · ');
};

export const placeOf = (s: Signal): LatLng | null =>
  typeof s.lat === 'number' && typeof s.lng === 'number' ? { lat: s.lat, lng: s.lng } : null;

/** The business in full: the card, the map where it is, and the leads within 500 m. */
export const signalDetail = (
  s: Signal,
  parts: { card: HTMLElement; mapEl: HTMLElement; leads: readonly Lead[] },
): Child => {
  const at = placeOf(s);
  const nearby = at ? leadsNearby(parts.leads, leadLocations(parts.leads), at) : [];
  return [
    parts.card,
    at ? parts.mapEl : null,
    nearby.length
      ? section(
          { title: 'Leads nearby', count: nearby.length },
          nearby.map((n) =>
            row({
              title: leadTitle(n.lead),
              meta: formatDistance(n.meters),
              href: `#/leads/${encodeURIComponent(n.lead.id)}`,
            }),
          ),
        )
      : null,
  ];
};
